import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import PostCard from '../components/PostCard';
import { config } from '../../config';

export default function Feed() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [newPostContent, setNewPostContent] = useState('');
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [mediaType, setMediaType] = useState('texte');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const lastPostRef = useRef();
  const fileInputRef = useRef(null);

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const API_BASE = config.apiBase;
  const UPLOADS_BASE = config.uploadsBase;

  // Fonction pour normaliser l'URL d'un média
  const normalizeMediaUrl = (mediaUrl) => {
    if (!mediaUrl) return '';
    if (mediaUrl.startsWith('http://') || mediaUrl.startsWith('https://')) {
      return mediaUrl;
    }
    if (mediaUrl.startsWith('/uploads/')) {
      const relativePath = mediaUrl.replace('/uploads/', '');
      return `${UPLOADS_BASE}/${relativePath}`;
    }
    return `${UPLOADS_BASE}/posts/${mediaUrl}`;
  };

  // Fonction de partage avec API Web Share
  const handleShare = async (post) => {
    const mediaUrl = normalizeMediaUrl(post.media_url);
    const postUrl = `${window.location.origin}/post/${post.id_post}`;
    const authorName = [post.prenom, post.nom].filter(Boolean).join(' ') || post.pseudo || 'IPTIC';
    const signature = 'D\u00e9velopp\u00e9e par IPTIC';
    const shareText = `${post.contenu || 'D\u00e9couvrez cette publication'}\n\n${signature}`;
    const shareUrl = mediaUrl || postUrl;

    const copyFallback = async () => {
      await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
      toast.success('Lien copi\u00e9 dans le presse-papier !');
    };

    if (!navigator.share) {
      try {
        await copyFallback();
      } catch (err) {
        toast.error('Impossible de copier le lien');
      }
      return;
    }

    try {
      const baseShareData = {
        title: `IPTIC Dosso - Publication de ${authorName}`,
        text: shareText,
        url: shareUrl
      };

      if (post.type_media === 'image' && mediaUrl && navigator.canShare) {
        try {
          const mediaResponse = await fetch(mediaUrl);
          const blob = await mediaResponse.blob();
          const extension = (blob.type.split('/')[1] || 'jpg').replace('jpeg', 'jpg');
          const file = new File([blob], `iptic-dosso-${post.id_post}.${extension}`, {
            type: blob.type || 'image/jpeg'
          });
          const fileShareData = {
            title: baseShareData.title,
            text: baseShareData.text,
            files: [file]
          };
          if (navigator.canShare(fileShareData)) {
            await navigator.share(fileShareData);
            toast.success('Partage r\u00e9ussi avec le m\u00e9dia !');
            return;
          }
        } catch (mediaError) {
          console.warn('Partage image indisponible, partage du lien utilis\u00e9:', mediaError);
        }
      }

      await navigator.share(baseShareData);
      toast.success('Partage r\u00e9ussi !');
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Erreur de partage:', error);
        try {
          await copyFallback();
        } catch (copyError) {
          toast.error('Erreur lors du partage');
        }
      }
    }
  };
  const fetchPosts = async (reset = false) => {
    if (loading) return;
    setLoading(true);
    
    const currentOffset = reset ? 0 : offset;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/posts/get_posts.php?limit=20&offset=${currentOffset}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success) {
        const normalizedPosts = (data.posts || []).map(post => ({
          ...post,
          media_url: config.normalizePostMedia(post.media_url),
          photo_profil: config.normalizeProfilePhoto(post.photo_profil)
        }));
        
        if (reset) {
          setPosts(normalizedPosts);
          setOffset(20);
        } else {
          setPosts(prev => [...prev, ...normalizedPosts]);
          setOffset(prev => prev + 20);
        }
        setHasMore(data.has_more);
      }
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePost = async () => {
    if (!newPostContent.trim() && !selectedMedia) {
      toast.error('Écrivez quelque chose ou ajoutez un média');
      return;
    }

    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('contenu', newPostContent);
      formData.append('type_media', mediaType);
      
      if (selectedMedia) {
        formData.append('media_file', selectedMedia);
      }

      const response = await fetch(`${API_BASE}/posts/create_post.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Post publié');
        setNewPostContent('');
        setSelectedMedia(null);
        setMediaType('texte');
        setShowCreateModal(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
        fetchPosts(true);
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de la publication');
    }
    setSubmitting(false);
  };

  const handleDeletePost = async (id_post) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/posts/delete_post.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ id_post })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Post supprimé');
        fetchPosts(true);
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleLike = async (id_post) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/posts/like_post.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ id_post })
      });
      const data = await response.json();
      if (data.success) {
        setPosts(prev => prev.map(post => 
          post.id_post === id_post 
            ? { ...post, likes: data.likes, user_liked: data.liked }
            : post
        ));
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const maxSize = 50 * 1024 * 1024;
      if (file.size > maxSize) {
        toast.error('Fichier trop volumineux (max 50 Mo)');
        return;
      }
      setSelectedMedia(file);
      
      if (file.type.startsWith('image/')) {
        setMediaType('image');
      } else if (file.type.startsWith('video/')) {
        setMediaType('video');
      } else if (file.type === 'application/pdf') {
        setMediaType('pdf');
      }
    }
  };

  useEffect(() => {
    fetchPosts(true);
  }, []);

  useEffect(() => {
    if (!lastPostRef.current || !hasMore || loading) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          fetchPosts();
        }
      },
      { threshold: 0.5 }
    );
    
    observer.observe(lastPostRef.current);
    return () => observer.disconnect();
  }, [posts, hasMore, loading]);

  return (
    <div className="min-h-screen bg-gray-100" style={{ fontFamily: "'Patrick Hand', 'Caveat', 'Comic Neue', cursive" }}>
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white shadow-sm border-b border-gray-200">
        <div className="w-full px-4 py-3 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="text-2xl">✨</div>
            <h1 className="text-lg md:text-2xl font-bold text-blue-700 tracking-wide">
              NIGER INFORMATIONS
            </h1>
          </div>
        </div>
      </div>

      {/* Contenu */}
      <div className="w-full">
        {posts.map((post, index) => (
          <div key={post.id_post} ref={index === posts.length - 1 ? lastPostRef : null} className="w-full">
            <PostCard
              post={post}
              currentUserId={user.id_utilisateur || user.id}
              onLike={handleLike}
              onDelete={handleDeletePost}
              onShare={handleShare}
            />
          </div>
        ))}
        
        {loading && (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent"></div>
          </div>
        )}
        
        {!hasMore && posts.length > 0 && (
          <div className="text-center py-6 text-gray-400 text-sm border-t border-gray-200">
            ✨ Fin du fil d'actualité ✨
          </div>
        )}
        
        {posts.length === 0 && !loading && (
          <div className="text-center py-16 bg-white border border-gray-200 mx-4 my-8">
            <div className="text-5xl mb-3">📭</div>
            <p className="text-gray-500 text-base md:text-lg">Aucun post pour le moment</p>
          </div>
        )}
      </div>

      {/* Modal création post */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex justify-between items-center">
              <h2 className="text-lg md:text-xl font-semibold text-gray-800">Créer une publication</h2>
              <button onClick={() => setShowCreateModal(false)} className="text-gray-500 text-2xl leading-none">&times;</button>
            </div>
            
            <div className="p-4 space-y-4">
              <textarea
                value={newPostContent}
                onChange={(e) => setNewPostContent(e.target.value)}
                placeholder="Quoi de neuf ?"
                rows="4"
                className="w-full border border-gray-300 p-3 text-base md:text-lg focus:outline-none focus:border-blue-500 transition"
                style={{ fontFamily: "'Patrick Hand', 'Caveat', cursive" }}
              />
              
              <div className="flex gap-3">
                <button 
                  type="button"
                  onClick={() => document.getElementById('mediaInput').click()}
                  className="flex-1 py-2 text-sm md:text-base font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 transition"
                >
                  📎 Ajouter un média
                </button>
                <input
                  id="mediaInput"
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,video/*,.pdf"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </div>
              
              {selectedMedia && (
                <div className="bg-gray-50 p-3 flex justify-between items-center border border-gray-200">
                  <div className="flex items-center gap-2">
                    <span className="text-xl md:text-2xl">
                      {selectedMedia.type.startsWith('image/') ? '🖼️' : 
                       selectedMedia.type.startsWith('video/') ? '🎬' : '📄'}
                    </span>
                    <span className="text-sm md:text-base truncate max-w-[200px]">{selectedMedia.name}</span>
                    <span className="text-xs text-gray-400">
                      {(selectedMedia.size / (1024 * 1024)).toFixed(2)} Mo
                    </span>
                  </div>
                  <button 
                    onClick={() => { setSelectedMedia(null); if (fileInputRef.current) fileInputRef.current.value = ''; }} 
                    className="text-red-500 hover:text-red-700 text-sm md:text-base"
                  >
                    ✕
                  </button>
                </div>
              )}
              
              <button 
                onClick={handleCreatePost} 
                disabled={submitting} 
                className="w-full bg-blue-600 text-white py-3 text-base md:text-lg font-semibold hover:bg-blue-700 disabled:opacity-50 transition"
              >
                {submitting ? 'Publication...' : 'Publier'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}